import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { LookalikeView, LookalikeWeightUpdate } from '@repo/contracts';
import {
  applyWeightUpdates,
  materializeLookalikes,
} from '@repo/persona-lookalikes/web';

import { isInsufficientCredits } from '../api/client';
import {
  generateLookalikes,
  getLookalikes,
  lookalikeKeys,
  updateLookalikeWeights,
} from '../api/persona-lookalikes';
import { invalidateCredits } from '../credits/use-credits';
import { logger } from '../lib/logger';

const log = logger.child({ module: 'persona-lookalikes' });

function generateErrorMessage(err: unknown): string {
  if (isInsufficientCredits(err)) {
    return "You're out of credits — top up to generate lookalikes.";
  }
  return err instanceof Error ? err.message : 'Could not generate lookalikes.';
}

/**
 * One persona's lookalike population.
 *
 * `updateWeights` is optimistic and *exactly* re-derivable: `applyWeightUpdates`
 * and `materializeLookalikes` are the same pure functions the server runs
 * (`@repo/persona-lookalikes`, imported here via its `/web` source subpath —
 * see that package's own doc on why), given the same seed. So the slider the
 * researcher is dragging updates instantly from local computation, and the
 * PATCH that follows normally just confirms what's already on screen rather
 * than replacing it.
 */
export function usePersonaLookalikes(personaId: string | null) {
  const queryClient = useQueryClient();
  const key = lookalikeKeys.forPersona(personaId ?? 'none');

  const query = useQuery({
    enabled: Boolean(personaId),
    queryFn: () => getLookalikes(personaId!),
    queryKey: key,
  });

  const write = (view: LookalikeView | null) => {
    queryClient.setQueryData<LookalikeView | null>(key, view);
  };

  const generate = useMutation({
    mutationFn: (count?: number) => generateLookalikes(personaId!, { count }),
    onError: (err) => {
      log.error({ err }, 'could not generate lookalikes');
    },
    onSuccess: (view) => {
      invalidateCredits(queryClient);
      write(view);
    },
  });

  const updateWeights = useMutation({
    mutationFn: (updates: LookalikeWeightUpdate[]) =>
      updateLookalikeWeights(personaId!, updates),
    // Declared before onError: TanStack infers the rollback context from this
    // callback's return type, and an onError written above it sees `{}` — see
    // `use-personas.ts`'s own note on this.
    onMutate: (updates) => {
      const previous = queryClient.getQueryData<LookalikeView | null>(key);
      if (!previous) return { previous };
      try {
        const schema = applyWeightUpdates(previous.spec.schema, updates);
        const { clusters, lookalikes } = materializeLookalikes(
          previous.spec.id,
          schema,
          previous.spec.sampleSeed,
          previous.spec.count,
        );
        write({
          clusters,
          lookalikes,
          spec: { ...previous.spec, schema },
        });
      } catch (err) {
        // An update naming a value that no longer exists in the pool — the
        // server will reject it too; just skip the optimistic step and let
        // the mutation's own error handling take over.
        log.warn({ err }, 'skipped an optimistic re-sample');
      }
      return { previous };
    },
    onError: (err, _updates, context) => {
      log.error({ err }, 'could not update lookalike weights');
      if (context?.previous) write(context.previous);
    },
    onSuccess: (view) => {
      write(view);
    },
  });

  return {
    generate: (count?: number) => generate.mutate(count),
    generateError: generate.error ? generateErrorMessage(generate.error) : null,
    generating: generate.isPending,
    loading: query.isPending && Boolean(personaId),
    updateWeights: (updates: LookalikeWeightUpdate[]) => {
      updateWeights.mutate(updates);
    },
    updatingWeights: updateWeights.isPending,
    view: query.data ?? null,
  };
}
